// src/components/MagazineSalesSection.js (FINAL, FULLY FUNCTIONAL VERSION)

import React, { useState, useEffect } from 'react';
import { api } from '../../apiService';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';
import * as Sentry from "@sentry/react";

const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

const getClientInitials = (name) => {
    if (!name) return '?';
    const words = name.split(' ');
    if (words.length > 1 && words[1]) return `${words[0][0]}${words[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

const formatDateOnly = (d) => {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
};

const parseItems = (items) => {
    if (Array.isArray(items)) return items;
    if (typeof items === 'string' && items.trim()) {
        try { return JSON.parse(items); } catch { return []; }
    }
    return [];
};

const createOrUpdateInvoiceForSale = async (saleData, saleId, client) => {
    if (!client) { toast.error("Client data missing for invoice."); return; }
    const existingInvoices = await api.get('magazine_invoices', { where: { saleId }, limit: 1 });
    const payments = await api.get('magazine_payments', { where: { saleId } }).catch(() => []);
    const totalPaid = (payments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const pendingAmount = saleData.totalAmount - totalPaid;
    let status = "Unpaid";
    if (pendingAmount <= 0) status = "Paid";
    else if (totalPaid > 0) status = "Partially Paid";
    const invoiceData = {
        clientId: client.id, clientName: client.name, clientCompany: client.companyName,
        clientAddress: client.companyAddress, saleId: saleId, saleName: saleData.title,
        items: saleData.items, totalAmount: saleData.totalAmount, invoiceDate: new Date().toISOString(),
        status, totalPaid, pendingAmount, startDate: saleData.startDate, endDate: saleData.endDate, updatedAt: new Date().toISOString()
    };
    try {
        if (existingInvoices && existingInvoices.length > 0) { await api.put('magazine_invoices', existingInvoices[0].id, invoiceData); }
        else { await api.post('magazine_invoices', { ...invoiceData, createdAt: new Date().toISOString() }); }
    } catch (error) { toast.error("Failed to sync invoice."); Sentry.captureException(error); }
};


// --- VIEW 1: SalesOrderForm ---
function SalesOrderForm({ clients, onBack, saleToEdit }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [title, setTitle] = useState('');
    const [clientId, setClientId] = useState('');
    const [billingType, setBillingType] = useState('duration');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [items, setItems] = useState([{ service: 'Magazine Ad', description: '', amount: '' }]);
    const [totalAmount, setTotalAmount] = useState(0);

    useEffect(() => {
        if (saleToEdit) {
            setTitle(saleToEdit.title || ''); setClientId(saleToEdit.clientId || '');
            setBillingType(saleToEdit.billingType || 'duration');
            setStartDate(saleToEdit.startDate ? formatDateOnly(saleToEdit.startDate) : '');
            setEndDate(saleToEdit.endDate ? formatDateOnly(saleToEdit.endDate) : '');
            if (saleToEdit.startDate) {
                const d = new Date(saleToEdit.startDate);
                setMonth(d.getMonth() + 1);
                setYear(d.getFullYear());
            }
            const parsedItems = parseItems(saleToEdit.items);
            setItems(parsedItems.length > 0 ? parsedItems : [{ service: 'Magazine Ad', description: '', amount: '' }]);
        }
    }, [saleToEdit]);

    useEffect(() => { const total = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0); setTotalAmount(total); }, [items]);

    const handleItemChange = (index, field, value) => { const newItems = [...items]; newItems[index][field] = value; setItems(newItems); };
    const addItem = () => setItems([...items, { service: 'Magazine Ad', description: '', amount: '' }]);
    const removeItem = (index) => { if (items.length > 1) setItems(items.filter((_, i) => i !== index)); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        let finalStartDate, finalEndDate;
        if (billingType === 'monthly') {
            finalStartDate = formatDateOnly(new Date(year, month - 1, 1));
            finalEndDate = formatDateOnly(new Date(year, month, 0));
        } else {
            finalStartDate = formatDateOnly(new Date(startDate));
            finalEndDate = formatDateOnly(new Date(endDate));
        }

        const filteredItems = items.filter(i => i.description && i.amount);
        const dataToSave = { clientId, title, billingType, startDate: finalStartDate, endDate: finalEndDate, items: JSON.stringify(filteredItems), totalAmount, updatedAt: new Date().toISOString() };
        try {
            let saleId;
            const client = clients.find(c => c.id === clientId);
            if (saleToEdit && saleToEdit.id) {
                saleId = saleToEdit.id;
                await api.put('magazine_sales_orders', saleId, dataToSave);
            } else {
                dataToSave.createdAt = new Date().toISOString();
                const res = await api.post('magazine_sales_orders', dataToSave);
                saleId = res.id;
            }
            await createOrUpdateInvoiceForSale({ ...dataToSave, totalAmount, startDate: finalStartDate, endDate: finalEndDate, items: dataToSave.items }, saleId, client);
            toast.success('Sale and Invoice saved!');
            onBack();
        } catch (err) { toast.error("Failed to save sale order."); Sentry.captureException(err); } 
        finally { setIsSubmitting(false); }
    };
    
    return ( <div className="container mx-auto"><div className="mb-8"><button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-rose-600 hover:text-rose-800"><Icon path="M10 19l-7-7m0 0l7-7m-7 7h18" /> Back</button><h1 className="text-4xl font-bold text-gray-800 mt-2">{saleToEdit ? 'Edit Sale Order' : 'Create New Sale Order'}</h1></div><form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-md space-y-6"><div><h2 className="text-xl font-bold text-gray-700 border-b pb-3">Order Details</h2><div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4"><div><label>Client</label><select value={clientId} onChange={e => setClientId(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" required><option value="">Select Client</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div><div><label>Order Title</label><input type="text" value={title} onChange={e => setTitle(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" required /></div><div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-2">Billing Type</label><div className="flex gap-4">{['duration', 'monthly'].map(type => (<label key={type} className="flex items-center gap-2"><input type="radio" name="billingType" value={type} checked={billingType === type} onChange={e => setBillingType(e.target.value)} className="h-4 w-4 text-red-600"/>{type.charAt(0).toUpperCase() + type.slice(1)}</label>))}</div></div>{billingType === 'duration' ? (<><div><label>Start Date</label><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" required /></div><div><label>End Date</label><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" required /></div></>) : (<><div className="md:col-span-1"><label>Month</label><select value={month} onChange={e => setMonth(Number(e.target.value))} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border">{Array.from({ length: 12 }, (_, i) => <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}</select></div><div className="md:col-span-1"><label>Year</label><select value={year} onChange={e => setYear(Number(e.target.value))} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border">{Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}</select></div></>)}</div></div><div className="pt-6 border-t"><h2 className="text-xl font-bold text-gray-700 mb-4">Services / Items</h2><div className="space-y-4">{items.map((item, index) => (<div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center bg-gray-50 p-4 rounded-lg border"><div className="md:col-span-3"><label className="text-xs font-semibold">Service</label><select value={item.service} onChange={e => handleItemChange(index, 'service', e.target.value)} className="mt-1 w-full p-2 border rounded-md"><option>Magazine Ad</option><option>Website Ad</option><option>Website News</option><option>Guest Posting</option><option>Other</option></select></div><div className="md:col-span-6"><label className="text-xs font-semibold">Description</label><input type="text" value={item.description} onChange={e => handleItemChange(index, 'description', e.target.value)} className="mt-1 w-full p-2 border rounded-md" required /></div><div className="md:col-span-2"><label className="text-xs font-semibold">Amount</label><input type="number" value={item.amount} onChange={e => handleItemChange(index, 'amount', e.target.value)} className="mt-1 w-full p-2 border rounded-md" required /></div><div className="md:col-span-1"><button type="button" onClick={() => removeItem(index)} className="w-full bg-red-500 text-white p-2 rounded-md mt-4">X</button></div></div>))}</div><button type="button" onClick={addItem} className="mt-4 bg-gray-200 font-semibold py-2 px-4 rounded-lg hover:bg-gray-300">+ Add Item</button></div><div className="flex justify-end items-center pt-6 border-t"><div className="text-right mr-6"><p className="text-gray-500">Total Order Amount</p><p className="text-3xl font-bold text-gray-800">PKR {totalAmount.toLocaleString()}</p></div><button type="submit" disabled={isSubmitting} className="text-white font-bold py-3 px-10 rounded-lg shadow-lg" style={{backgroundColor: isSubmitting ? '#a11217' : '#c4151c'}}>{isSubmitting ? 'Saving...' : 'Save & Create Invoice'}</button></div></form></div> );
}

// --- VIEW 2: ClientSalesHistory ---
function ClientSalesHistory({ client, onBack, onEditSale, onDeleteSale }) {
    const [sales, setSales] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!client) return;
        setLoading(true);
        Promise.all([
            api.get('magazine_sales_orders', { where: { clientId: client.id }, orderBy: 'createdAt DESC' }),
            api.get('magazine_payments', { where: { clientId: client.id } }).catch(() => [])
        ]).then(([salesRows, paymentRows]) => {
            const normalizedSales = (salesRows || []).map(s => ({ ...s, totalAmount: Number(s.totalAmount) || 0, items: parseItems(s.items) }));
            const normalizedPayments = (paymentRows || []).map(p => ({ ...p, amount: Number(p.amount) || 0 }));
            setSales(normalizedSales);
            setPayments(normalizedPayments);
        }).catch((err) => {
            toast.error(`Failed to load sales: ${err.message}`);
            setSales([]);
            setPayments([]);
        }).finally(() => setLoading(false));
    }, [client]);

    const getSaleMonth = (sale) => {
        if (!sale.startDate) return 'N/A';
        return new Date(sale.startDate).toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    return (
        <div className="container mx-auto">
            <div className="mb-8"><button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-rose-600 hover:text-rose-800"><Icon path="M10 19l-7-7m0 0l7-7m-7 7h18" /> Back to Client List</button><h1 className="text-4xl font-bold text-gray-800 mt-2">{client.name}'s Sales History</h1><p className="text-gray-500 mt-1">{client.companyName}</p></div>
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-xs text-gray-500 uppercase bg-gray-50"><tr><th className="px-6 py-3 text-left">Order Title</th><th className="px-6 py-3 text-left">Month/Duration</th><th className="px-6 py-3 text-right">Amount</th><th className="px-6 py-3 text-right">Received</th><th className="px-6 py-3 text-right">Pending</th><th className="px-6 py-3 text-center">Actions</th></tr></thead>
                        <tbody className="divide-y divide-gray-200">
                        {loading ? (<tr><td colSpan="6" className="text-center p-8 text-gray-500">Loading...</td></tr>) 
                        : sales.length > 0 ? sales.map(sale => {
                            const totalPaid = payments.filter(p => p.saleId === sale.id).reduce((sum, p) => sum + p.amount, 0);
                            const pending = sale.totalAmount - totalPaid;
                            return (<tr key={sale.id} className="hover:bg-gray-50"><td className="px-6 py-4"><div className="font-bold text-gray-800">{sale.title}</div><div className="text-xs text-gray-500">{sale.items.map(i => i.service).join(', ')}</div></td><td className="px-6 py-4 text-gray-600">{sale.billingType === 'monthly' ? getSaleMonth(sale) : `${new Date(sale.startDate).toLocaleDateString()} - ${new Date(sale.endDate).toLocaleDateString()}`}</td><td className="px-6 py-4 text-right font-semibold">PKR {sale.totalAmount.toLocaleString()}</td><td className="px-6 py-4 text-right font-semibold text-green-600">PKR {totalPaid.toLocaleString()}</td><td className="px-6 py-4 text-right font-semibold text-red-600">PKR {pending.toLocaleString()}</td><td className="px-6 py-4 text-center whitespace-nowrap"><button onClick={() => onEditSale(sale)} className="font-semibold text-blue-600 hover:text-blue-800">Edit</button><span className="text-gray-300 mx-2">|</span><button onClick={() => onDeleteSale(sale)} className="font-semibold text-red-600 hover:text-red-800">Delete</button></td></tr>)
                        }) : (<tr><td colSpan="6" className="text-center p-12 text-gray-500">No sales orders found for this client.</td></tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// --- VIEW 3: Main Client List View ---
export default function MagazineSalesSection() {
    const [allClients, setAllClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [clientSalesData, setClientSalesData] = useState({});
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list');
    const [editingSale, setEditingSale] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [deletingSale, setDeletingSale] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [clientsLoaded, setClientsLoaded] = useState(false);
    const [allSalesOrders, setAllSalesOrders] = useState([]);
    
    useEffect(() => {
        let mounted = true;
        api.get('magazine_clients')
            .then((rows) => { if (mounted) { setAllClients(rows || []); setClientsLoaded(true); } })
            .catch((err) => { toast.error(`Failed to load clients: ${err.message}`); })
            .finally(() => { if (mounted) setClientsLoaded(true); });
        return () => { mounted = false; };
    }, []);
    
    useEffect(() => {
        let mounted = true;
        api.get('magazine_sales_orders')
            .then((rows) => { if (mounted) setAllSalesOrders((rows || []).map(s => ({ ...s, totalAmount: Number(s.totalAmount) || 0 }))); })
            .catch(() => { if (mounted) setAllSalesOrders([]); });
        return () => { mounted = false; };
    }, []);
    
    useEffect(() => {
        if (!clientsLoaded) return;
        const fetchAndFilter = async () => {
            setLoading(true);
            try {
                const startOfMonth = new Date(filterYear, filterMonth - 1, 1);
                const endOfMonth = new Date(filterYear, filterMonth, 0, 23, 59, 59);
                const salesThisMonth = allSalesOrders.filter(s => {
                    const d = new Date(s.startDate);
                    return d >= startOfMonth && d <= endOfMonth;
                });
                const salesDataByClient = salesThisMonth.reduce((acc, sale) => {
                    if (!acc[sale.clientId]) acc[sale.clientId] = { count: 0, totalAmount: 0 };
                    acc[sale.clientId].count += 1;
                    acc[sale.clientId].totalAmount += sale.totalAmount;
                    return acc;
                }, {});
                setClientSalesData(salesDataByClient);
                let clientsToShow = allClients.filter(client => salesDataByClient[client.id]);
                if (searchTerm) {
                    const lowerSearch = searchTerm.toLowerCase();
                    clientsToShow = allClients.filter(client => (client.name.toLowerCase().includes(lowerSearch) || client.companyName.toLowerCase().includes(lowerSearch)) );
                }
                setFilteredClients(clientsToShow);
            } catch(e) { toast.error("Could not filter data."); } 
            finally { setLoading(false); }
        };
        fetchAndFilter();
    }, [allClients, allSalesOrders, clientsLoaded, searchTerm, filterMonth, filterYear]);

    const handleEditSale = (sale) => { setSelectedClient(null); setEditingSale(sale); setView('form'); };
    const handleAddNewSale = () => { setEditingSale(null); setView('form'); };
    
    const handleDeleteSale = async () => {
        if (!deletingSale) return;
        try {
            await api.delete('magazine_sales_orders', deletingSale.id);
            const invoiceRows = await api.get('magazine_invoices', { where: { saleId: deletingSale.id } }).catch(() => []);
            await Promise.all((invoiceRows || []).map(inv => api.delete('magazine_invoices', inv.id)));
            const paymentRows = await api.get('magazine_payments', { where: { saleId: deletingSale.id } }).catch(() => []);
            await Promise.all((paymentRows || []).map(p => api.delete('magazine_payments', p.id)));
            toast.success("Sale and invoice deleted.");
            setAllSalesOrders(prev => prev.filter(s => String(s.id) !== String(deletingSale.id)));
        } catch(err) {
            toast.error("Failed to delete.");
        } finally {
            setDeletingSale(null);
        }
    };

    if (view === 'form') { return <SalesOrderForm clients={allClients} onBack={() => { setView('list'); setEditingSale(null); setSelectedClient(null); }} saleToEdit={editingSale} />; }
    
    if (selectedClient) { 
        return <ClientSalesHistory 
            client={selectedClient} 
            onBack={() => setSelectedClient(null)} 
            onEditSale={handleEditSale} 
            onDeleteSale={setDeletingSale}
        />; 
    }

    return (
        <div className="container mx-auto">
            <ConfirmationModal isOpen={!!deletingSale} onClose={() => setDeletingSale(null)} onConfirm={handleDeleteSale} title="Delete Sale Order">Are you sure you want to delete the order "<strong>{deletingSale?.title}</strong>"? This will also delete the associated invoice.</ConfirmationModal>
            <div className="mb-8 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                <div><h1 className="text-4xl font-bold text-gray-800">Sales Orders</h1><p className="text-gray-500 mt-1">Create new sale orders for Ad Clients and generate invoices.</p></div>
                <button onClick={handleAddNewSale} className="text-white font-bold py-3 px-6 rounded-lg shadow-lg flex items-center gap-2" style={{backgroundColor: '#c4151c'}}><Icon path="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /><span>Create New Sale Order</span></button>
            </div>
            
            <div className="bg-white p-4 rounded-xl shadow-md mb-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div className="md:col-span-2"><label className="text-sm font-medium text-gray-600">Search by Client Name</label><input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="mt-1 w-full bg-gray-50 p-3 rounded-lg border"/></div>
                    <div><label className="text-sm font-medium text-gray-600">Month</label><select value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border">{Array.from({ length: 12 }, (_, i) => <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}</select></div>
                    <div><label className="text-sm font-medium text-gray-600">Year</label><select value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border">{Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}</select></div>
                </div>
            </div>
            
            {loading ? <div className="text-center p-8">Loading data...</div> : 
            filteredClients.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredClients.map(client => {
                        const salesInfo = clientSalesData[client.id] || { count: 0, totalAmount: 0 };
                        return (
                            <div key={client.id} onClick={() => setSelectedClient(client)} className="bg-white rounded-xl shadow-md p-6 cursor-pointer transition-all hover:shadow-xl hover:-translate-y-1">
                                <div className="flex items-center gap-4">
                                    <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full text-white font-bold text-lg" style={{backgroundColor: '#c4151c'}}>{getClientInitials(client.name)}</div>
                                    <div><h3 className="text-lg font-bold text-gray-800 truncate">{client.name}</h3><p className="text-sm text-gray-500">{client.companyName}</p></div>
                                </div>
                                <div className="mt-4 pt-4 border-t flex justify-between text-sm">
                                    <div className="text-center"><p className="font-bold text-gray-800">{salesInfo.count}</p><p className="text-gray-500">Sale(s) this month</p></div>
                                    <div className="text-center"><p className="font-bold text-gray-800">PKR {salesInfo.totalAmount.toLocaleString()}</p><p className="text-gray-500">Amount</p></div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            ) : (<div className="text-center p-12 bg-white rounded-xl shadow-md"><p className="text-gray-500">No clients with sales found for the selected period/search.</p></div>)
            }
        </div>
    );
}
